import {Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {Company} from '../../../../models/company';
import {
    AddressLookupResult,
    cityFromLookup,
    provinceCodeFromLookup
} from '../../../../services/address-lookup/address-lookup.service';

export type CompanyLegalDocumentField =
    'legalDocumentFront'
    | 'legalDocumentBack'
    | 'legalFiscalCodeFront'
    | 'legalFiscalCodeBack';

@Component({
    selector: 'app-companies-settings-legal',
    templateUrl: './companies-settings-legal.component.html',
    styles: [':host { display: contents !important; }']
})
export class CompaniesSettingsLegalComponent implements OnDestroy {
    @Input() company: Company = new Company();
    @Input() provinces: { id: string; name: string }[] = [];
    @Input() legalCities: string[] = [];

    @Output() legalProvinceChange = new EventEmitter<void>();

    private cityApplyTimeout: ReturnType<typeof setTimeout> | null = null;

    get fiscalCodeLength(): number {
        return (this.company.legalFiscalCode || '').replace(/\s+/g, '').length;
    }

    normalizeLegalFiscalCode(): void {
        if (typeof this.company.legalFiscalCode === 'string') {
            this.company.legalFiscalCode = this.company.legalFiscalCode.replace(/\s+/g, '').toUpperCase();
        }
    }

    selectAddress(result: AddressLookupResult): void {
        const address = result.address ?? {};
        if (address.house_number) {
            this.company.legalAddressNumber = address.house_number as unknown as number;
        }
        if (address.postcode) {
            this.company.legalZip = address.postcode;
        }

        const provinceCode = provinceCodeFromLookup(result, this.provinces);
        if (provinceCode) {
            this.company.legalProvince = provinceCode;
            this.legalProvinceChange.emit();
        }
        this.scheduleCityApply(cityFromLookup(result));
    }

    ngOnDestroy(): void {
        if (this.cityApplyTimeout) {
            clearTimeout(this.cityApplyTimeout);
        }
    }

    private scheduleCityApply(city?: string): void {
        if (!city) {
            return;
        }
        if (this.cityApplyTimeout) {
            clearTimeout(this.cityApplyTimeout);
        }
        this.cityApplyTimeout = setTimeout(() => {
            const normalized = this.normalizeName(city);
            this.company.legalCity = this.legalCities.find((option) => this.normalizeName(option) === normalized) ?? city;
            this.cityApplyTimeout = null;
        });
    }

    private normalizeName(value: string): string {
        return value
            .toLocaleLowerCase('it')
            .replace(/provincia (di|del|della) /g, '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim();
    }
}
